import { isProd } from 'common/tools/env';

import type { PlayerCustomData } from 'website/module/player';

export type GTMItemData = {
  action: string;
  dataLayerVariables: string[];
  event: string;
  eventCategory: string;
};

/**
 * This object simplifies the dataLayer's variables handling:
 *  - we use the human name of the variable's key as a key, and internally
 *  - each dimension defines its own getValue method
 *
 * @type {Object}
 */
const customVariables: {
  [key: string]: {
    getValue: (customData: PlayerCustomData) => string | number | null;
  };
} = {
  clicked_video_id: {
    getValue: ({ id }) => id ?? null
  },
  clicked_movie_id: {
    getValue: ({ relatedEntityType, relatedEntityId }) => {
      if (
        typeof relatedEntityType === 'string' &&
        relatedEntityType.toLowerCase() === 'movie'
      ) {
        return relatedEntityId ?? null;
      }

      return null;
    }
  },
  clicked_series_id: {
    getValue: ({ relatedEntityType, relatedEntityId }) => {
      if (
        typeof relatedEntityType === 'string' &&
        relatedEntityType.toLowerCase() === 'series'
      ) {
        return relatedEntityId ?? null;
      }

      return null;
    }
  },
  clicked_movie_label: {
    getValue: ({ relatedEntityType, relatedEntityTitle }) => {
      if (
        typeof relatedEntityType === 'string' &&
        relatedEntityType.toLowerCase() === 'movie'
      ) {
        return relatedEntityTitle ?? null;
      }

      return null;
    }
  },
  clicked_series_label: {
    getValue: ({ relatedEntityType, relatedEntityTitle }) => {
      if (
        typeof relatedEntityType === 'string' &&
        relatedEntityType.toLowerCase() === 'series'
      ) {
        return relatedEntityTitle ?? null;
      }

      return null;
    }
  },
  clicked_video_label: {
    getValue: ({ title }) => title ?? null
  },
  movie_distributor: {
    getValue: ({ relatedEntityType, relatedEntityDistributorId }) => {
      if (
        typeof relatedEntityType === 'string' &&
        relatedEntityType.toLowerCase() === 'movie'
      ) {
        return relatedEntityDistributorId ?? null;
      }

      return null;
    }
  },
  series_distributor: {
    getValue: ({ relatedEntityType, relatedEntityDistributorId }) => {
      if (
        typeof relatedEntityType === 'string' &&
        relatedEntityType.toLowerCase() === 'series'
      ) {
        return relatedEntityDistributorId ?? null;
      }

      return null;
    }
  }
};

/**
 * This function is used to push generate dataLayer's variables with customVariables and push data into dataLayer's object
 * @param  {String} action               The event action
 * @param  {String} event                The event name
 * @param  {String} eventCategory        The event category
 * @param  {Array} dataLayerVariables    An array of variables to send
 * @param  {PlayerCustomData} customData Current video tracking data
 * @return {void}
 */
export const pushDatalayer = (
  { action, event, eventCategory, dataLayerVariables = [] }: GTMItemData,
  customData: PlayerCustomData
) => {
  if (window.dataLayer && dataLayerVariables.length > 0) {
    const eventVariables: Record<string, string | number | null> = {};
    // parse all dataLayer's variables
    for (const dataLayerVariableKey of dataLayerVariables) {
      const customVariable = customVariables[dataLayerVariableKey];
      if (customVariable) {
        const resultVariable = customVariable.getValue(customData);
        if (resultVariable) {
          eventVariables[dataLayerVariableKey] = resultVariable;
        }
      } else {
        window.console.warn(`Unknown variable's Name ${dataLayerVariableKey}`);
      }
    }

    if (isProd()) {
      window.dataLayer.push({
        event: event,
        eventAction: action,
        eventCategory: eventCategory,
        ...eventVariables
      });
    }
  }
};
